import React, { useEffect, useState } from 'react';
import { Container, Table, Dropdown, DropdownButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { LICENCE_CATALOGUE } from '../data/allowedLicenses';
import { iconSetCredits } from '../utils/iconSetCredits';

export default function CreditsAndLicensesScreen() {
  const { t } = useTranslation();

  const [rows, setRows] = useState(null); // holds the icon sets data displayed inside the table
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState('');
  const [text, setText] = useState(''); // holds the license text
  const [licenseLoadingError, setLicenseLoadingError] = useState(null);
  const [loading, setLoading] = useState(false);

  const DATA_URL = 'https://api.iconify.design/collections?pretty=0'; // the iconify api endpoint for the icon sets data

  useEffect(() => {
    fetch(DATA_URL)
      .then((r) => r.json()) // get the icon sets data from iconify
      .then(iconSetCredits) // the table's rows: the sets under an allowed licence, each with its author and licence, sorted by name
      .then(setRows) // store the data inside the rows state
      .catch((e) => setError(e.message));
  }, []); // this useEffect fetches the icon sets data from iconify and prepares it for displaying it in the table

  const handleLicenseSelect = async (selected) => {
    setSelected(selected);
    setText('');
    setLoading(true);
    setLicenseLoadingError(null);

    try {
      const res = await fetch(`/licenses/${LICENCE_CATALOGUE[selected].file}`);
      const raw = await res.text();
      setText(raw);
    } catch (err) {
      setLicenseLoadingError(
        t('credits-and-licenses-screen-license-fetch-error')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid="lg" className="icon-credits-container">
      <title>{t('credits-and-licenses-screen-helmet')}</title>

      <h1 className="screen-heading">
        {t('credits-and-licenses-screen-heading')}
      </h1>

      <h2>{t('credits-and-licenses-screen-icon-credits')}</h2>
      <p>{t('credits-and-licenses-screen-icon-credits-info')}</p>

      {rows ? (
        <Table className="icon-licenses-table" striped bordered hover size="sm">
          <thead>
            <tr>
              <th>{t('credits-and-licenses-screen-set')}</th>
              <th>{t('credits-and-licenses-screen-author')}</th>
              <th>{t('credits-and-licenses-screen-license')}</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.prefix}>
                <td>{r.name}</td>

                <td>
                  {!r.author ? (
                    t('credits-and-licenses-screen-unknown')
                  ) : r.author.url ? (
                    <a href={r.author.url} rel="noreferrer" target="_blank">
                      {r.author.name}
                    </a>
                  ) : (
                    r.author.name
                  )}
                </td>

                <td>
                  {r.license.url ? (
                    <a href={r.license.url} rel="noreferrer" target="_blank">
                      {r.license.title}
                    </a>
                  ) : (
                    r.license.title
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : error ? (
        <MessageBox className="danger-alert">
          {t('credits-and-licenses-screen-loading-icon-credits-error')}
          {error}
        </MessageBox>
      ) : (
        <div className="overlay">
          <LoadingLogo />
        </div>
      )}

      <div className="icon-licenses">
        <div>
          <h2>{t('credits-and-licenses-screen-licenses')}</h2>
          <p>{t('credits-and-licenses-screen-licenses-info')}</p>
        </div>

        <DropdownButton
          variant="secondary"
          align="end"
          id="license-dropdown"
          title={
            selected
              ? LICENCE_CATALOGUE[selected]?.title ||
                t('credits-and-licenses-screen-select-a-license')
              : t('credits-and-licenses-screen-select-a-license')
          }
          onSelect={handleLicenseSelect}
        >
          {Object.entries(LICENCE_CATALOGUE).map(([spdx, meta]) => (
            <Dropdown.Item key={spdx} eventKey={spdx}>
              {meta.title}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </div>

      {/* Render the selected license */}
      {selected && (
        <section className="icon-license-section">
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {loading
              ? t('credits-and-licenses-screen-license-loading')
              : licenseLoadingError
                ? licenseLoadingError
                : text}
          </pre>
        </section>
      )}
    </Container>
  );
}
